import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("polling"); // polling | done | error
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/billing", { replace: true });
      return;
    }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          await refreshUser();
          setState("done");
          toast.success("Payment successful — your plan is now active!");
          setTimeout(() => navigate("/billing", { replace: true }), 1800);
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) { /* retry */ }
      if (attempts >= 12) { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [navigate, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-5">
      <div className="text-center max-w-sm animate-rise">
        {state === "polling" && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-[#2563EB]" />
            <h1 className="mt-5 text-xl font-bold text-[#0F172A]">Confirming your payment…</h1>
            <p className="text-sm text-[#64748B] mt-1">This only takes a moment.</p>
          </>
        )}
        {state === "done" && (
          <>
            <span className="mx-auto h-16 w-16 rounded-2xl bg-[#ECFDF5] flex items-center justify-center"><CheckCircle2 size={32} className="text-[#059669]" /></span>
            <h1 className="mt-5 text-2xl font-extrabold text-[#0F172A]">You're all set!</h1>
            <p className="text-sm text-[#64748B] mt-1">Redirecting you to billing…</p>
          </>
        )}
        {state === "error" && (
          <>
            <span className="mx-auto h-16 w-16 rounded-2xl bg-[#FEF2F2] flex items-center justify-center"><XCircle size={32} className="text-[#DC2626]" /></span>
            <h1 className="mt-5 text-xl font-bold text-[#0F172A]">Payment not confirmed</h1>
            <p className="text-sm text-[#64748B] mt-1">If you completed checkout, it may take a minute. Otherwise please try again.</p>
            <button onClick={() => navigate("/billing", { replace: true })} className="mt-4 rounded-lg bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white">Back to billing</button>
          </>
        )}
      </div>
    </div>
  );
}
